import { AppState } from '../storage/repository';

export type BadgeCategory = 'all' | 'syllables' | 'words' | 'reading';

export interface MasteryBadge {
  id: string;
  title: string;
  subtitle: string;
  category: 'syllables' | 'words' | 'reading';
  moduleTarget: 'syllables' | 'wordbuilder' | 'camera' | 'mywords' | 'garden';
  icon: string;
  targetCount: number;
  currentCount: (state: AppState) => number;
  isUnlocked: (state: AppState) => boolean;
  requiredItemsDesc: string;
  unlockedSpeech: string;
  lockedSpeech: string;
  colorTheme: {
    badgeBg: string;
    border: string;
    badgeGlow: string;
    pillBg: string;
    textColor: string;
    accent: string;
  };
}

export const MASTERY_BADGES: MasteryBadge[] = [
  // --- 1. MODUL SUKU KATA ---
  {
    id: 'badge-syl-b',
    title: 'Juara Suku Kata B',
    subtitle: 'Keluarga BA, BI, BU, BE, BO',
    category: 'syllables',
    moduleTarget: 'syllables',
    icon: '🐝',
    targetCount: 5,
    currentCount: (state) => {
      const bSet = ['BA', 'BI', 'BU', 'BE', 'BO'];
      return state.progress.syllablesFamiliar.filter(s => bSet.includes(s.toUpperCase())).length;
    },
    isUnlocked: (state) => {
      const bSet = ['BA', 'BI', 'BU', 'BE', 'BO'];
      const familiar = state.progress.syllablesFamiliar.map(s => s.toUpperCase());
      return bSet.every(s => familiar.includes(s));
    },
    requiredItemsDesc: 'Pelajari 5 suku kata: BA, BI, BU, BE, BO',
    unlockedSpeech: 'Hebat sekali! Kamu meraih Lencana Juara Suku Kata B karena sudah menguasai Ba, Bi, Bu, Be, Bo!',
    lockedSpeech: 'Lencana Juara Suku Kata B. Sentuh Belajar Suku Kata dan bunyikan Ba, Bi, Bu, Be, dan Bo untuk membukanya!',
    colorTheme: {
      badgeBg: 'from-amber-400 to-yellow-500',
      border: 'border-amber-400',
      badgeGlow: 'rgba(251, 191, 36, 0.4)',
      pillBg: 'bg-amber-100 text-amber-900',
      textColor: 'text-amber-950',
      accent: '#F59E0B',
    },
  },
  {
    id: 'badge-syl-cd',
    title: 'Petualang C & D',
    subtitle: 'Keluarga CA, CI.. & DA, DI..',
    category: 'syllables',
    moduleTarget: 'syllables',
    icon: '🐬',
    targetCount: 10,
    currentCount: (state) => {
      const cdSet = ['CA', 'CI', 'CU', 'CE', 'CO', 'DA', 'DI', 'DU', 'DE', 'DO'];
      return state.progress.syllablesFamiliar.filter(s => cdSet.includes(s.toUpperCase())).length;
    },
    isUnlocked: (state) => {
      const cdSet = ['CA', 'CI', 'CU', 'CE', 'CO', 'DA', 'DI', 'DU', 'DE', 'DO'];
      const familiar = state.progress.syllablesFamiliar.map(s => s.toUpperCase());
      return cdSet.every(s => familiar.includes(s));
    },
    requiredItemsDesc: 'Pelajari keluarga suku kata C dan D lengkap (10 suku kata)',
    unlockedSpeech: 'Luar biasa! Kamu meraih Lencana Petualang C dan D!',
    lockedSpeech: 'Lencana Petualang C dan D. Ayo dengarkan suku kata C dan D di menu Suku Kata!',
    colorTheme: {
      badgeBg: 'from-cyan-400 to-blue-500',
      border: 'border-cyan-400',
      badgeGlow: 'rgba(6, 182, 212, 0.4)',
      pillBg: 'bg-cyan-100 text-cyan-900',
      textColor: 'text-cyan-950',
      accent: '#06B6D4',
    },
  },
  {
    id: 'badge-syl-km',
    title: 'Jagoan K & M',
    subtitle: 'Keluarga KA, KI.. & MA, MI..',
    category: 'syllables',
    moduleTarget: 'syllables',
    icon: '🦁',
    targetCount: 10,
    currentCount: (state) => {
      const kmSet = ['KA', 'KI', 'KU', 'KE', 'KO', 'MA', 'MI', 'MU', 'ME', 'MO'];
      return state.progress.syllablesFamiliar.filter(s => kmSet.includes(s.toUpperCase())).length;
    },
    isUnlocked: (state) => {
      const kmSet = ['KA', 'KI', 'KU', 'KE', 'KO', 'MA', 'MI', 'MU', 'ME', 'MO'];
      const familiar = state.progress.syllablesFamiliar.map(s => s.toUpperCase());
      return kmSet.every(s => familiar.includes(s));
    },
    requiredItemsDesc: 'Pelajari keluarga suku kata K dan M lengkap (10 suku kata)',
    unlockedSpeech: 'Keren sekali! Kamu meraih Lencana Jagoan K dan M!',
    lockedSpeech: 'Lencana Jagoan K dan M. Ayo pelajari suku kata K dan M di Belajar Suku Kata!',
    colorTheme: {
      badgeBg: 'from-orange-400 to-amber-500',
      border: 'border-orange-400',
      badgeGlow: 'rgba(249, 115, 22, 0.4)',
      pillBg: 'bg-orange-100 text-orange-900',
      textColor: 'text-orange-950',
      accent: '#F97316',
    },
  },
  {
    id: 'badge-syl-10',
    title: 'Kolektor 10 Suku Kata',
    subtitle: 'Jelajahi berbagai suku kata',
    category: 'syllables',
    moduleTarget: 'syllables',
    icon: '📖',
    targetCount: 10,
    currentCount: (state) => Math.min(10, state.progress.syllablesFamiliar.length),
    isUnlocked: (state) => state.progress.syllablesFamiliar.length >= 10,
    requiredItemsDesc: 'Kenali dan bunyikan 10 suku kata apa saja',
    unlockedSpeech: 'Selamat! Kamu sudah mengoleksi 10 suku kata membaca!',
    lockedSpeech: 'Kolektor 10 Suku Kata. Dengarkan 10 suku kata apa saja untuk membukanya!',
    colorTheme: {
      badgeBg: 'from-emerald-400 to-teal-500',
      border: 'border-emerald-400',
      badgeGlow: 'rgba(16, 185, 129, 0.4)',
      pillBg: 'bg-emerald-100 text-emerald-900',
      textColor: 'text-emerald-950',
      accent: '#10B981',
    },
  },

  // --- 2. MODUL SUSUN KATA ---
  {
    id: 'badge-word-food',
    title: 'Koki Kata Makanan',
    subtitle: 'Kata ROTI dan SUSU',
    category: 'words',
    moduleTarget: 'wordbuilder',
    icon: '🍞',
    targetCount: 2,
    currentCount: (state) => {
      const foodWords = ['ROTI', 'SUSU'];
      return state.progress.wordsMastered.filter(w => foodWords.includes(w.toUpperCase())).length;
    },
    isUnlocked: (state) => {
      const foodWords = ['ROTI', 'SUSU'];
      const mastered = state.progress.wordsMastered.map(w => w.toUpperCase());
      return foodWords.every(w => mastered.includes(w));
    },
    requiredItemsDesc: 'Susun kata ROTI dan SUSU di Game Susun Kata',
    unlockedSpeech: 'Nyam nyam! Kamu meraih Lencana Koki Kata Makanan setelah menyusun kata Roti dan Susu!',
    lockedSpeech: 'Lencana Koki Kata Makanan. Ayo susun kata Roti dan Susu di game Susun Kata!',
    colorTheme: {
      badgeBg: 'from-yellow-400 to-amber-500',
      border: 'border-yellow-400',
      badgeGlow: 'rgba(234, 179, 8, 0.4)',
      pillBg: 'bg-yellow-100 text-yellow-900',
      textColor: 'text-yellow-950',
      accent: '#EAB308',
    },
  },
  {
    id: 'badge-word-animals',
    title: 'Sahabat Hewan Cilik',
    subtitle: 'Kata KUDA dan SAPI',
    category: 'words',
    moduleTarget: 'wordbuilder',
    icon: '🐴',
    targetCount: 2,
    currentCount: (state) => {
      const animalWords = ['KUDA', 'SAPI'];
      return state.progress.wordsMastered.filter(w => animalWords.includes(w.toUpperCase())).length;
    },
    isUnlocked: (state) => {
      const animalWords = ['KUDA', 'SAPI'];
      const mastered = state.progress.wordsMastered.map(w => w.toUpperCase());
      return animalWords.every(w => mastered.includes(w));
    },
    requiredItemsDesc: 'Susun kata KUDA dan SAPI di Game Susun Kata',
    unlockedSpeech: 'Hore! Kamu meraih Lencana Sahabat Hewan Cilik setelah menyusun kata Kuda dan Sapi!',
    lockedSpeech: 'Lencana Sahabat Hewan Cilik. Ayo susun kata Kuda dan Sapi di game Susun Kata!',
    colorTheme: {
      badgeBg: 'from-green-400 to-emerald-600',
      border: 'border-green-400',
      badgeGlow: 'rgba(34, 197, 94, 0.4)',
      pillBg: 'bg-green-100 text-green-900',
      textColor: 'text-green-950',
      accent: '#22C55E',
    },
  },
  {
    id: 'badge-word-body',
    title: 'Pintar Mengenal Tubuh',
    subtitle: 'Kata KAKI dan MATA',
    category: 'words',
    moduleTarget: 'wordbuilder',
    icon: '👀',
    targetCount: 2,
    currentCount: (state) => {
      const bodyWords = ['KAKI', 'MATA'];
      return state.progress.wordsMastered.filter(w => bodyWords.includes(w.toUpperCase())).length;
    },
    isUnlocked: (state) => {
      const bodyWords = ['KAKI', 'MATA'];
      const mastered = state.progress.wordsMastered.map(w => w.toUpperCase());
      return bodyWords.every(w => mastered.includes(w));
    },
    requiredItemsDesc: 'Susun kata KAKI dan MATA di Game Susun Kata',
    unlockedSpeech: 'Pintar! Kamu meraih Lencana Pintar Mengenal Tubuh setelah menyusun kata Kaki dan Mata!',
    lockedSpeech: 'Lencana Pintar Mengenal Tubuh. Ayo susun kata Kaki dan Mata di game Susun Kata!',
    colorTheme: {
      badgeBg: 'from-sky-400 to-indigo-500',
      border: 'border-sky-400',
      badgeGlow: 'rgba(56, 189, 248, 0.4)',
      pillBg: 'bg-sky-100 text-sky-900',
      textColor: 'text-sky-950',
      accent: '#38BDF8',
    },
  },
  {
    id: 'badge-word-clothes',
    title: 'Pakaian & Sekolah',
    subtitle: 'Kata BAJU, TOPI, BUKU',
    category: 'words',
    moduleTarget: 'wordbuilder',
    icon: '👕',
    targetCount: 3,
    currentCount: (state) => {
      const items = ['BAJU', 'TOPI', 'BUKU'];
      return state.progress.wordsMastered.filter(w => items.includes(w.toUpperCase())).length;
    },
    isUnlocked: (state) => {
      const items = ['BAJU', 'TOPI', 'BUKU'];
      const mastered = state.progress.wordsMastered.map(w => w.toUpperCase());
      return items.every(w => mastered.includes(w));
    },
    requiredItemsDesc: 'Susun kata BAJU, TOPI, dan BUKU di Susun Kata',
    unlockedSpeech: 'Bagus sekali! Kamu meraih Lencana Modul Pakaian dan Sekolah!',
    lockedSpeech: 'Lencana Pakaian dan Sekolah. Selesaikan kata Baju, Topi, dan Buku!',
    colorTheme: {
      badgeBg: 'from-blue-500 to-indigo-600',
      border: 'border-blue-400',
      badgeGlow: 'rgba(79, 124, 247, 0.4)',
      pillBg: 'bg-blue-100 text-blue-900',
      textColor: 'text-blue-950',
      accent: '#4F7CF7',
    },
  },
  {
    id: 'badge-word-sepeda',
    title: 'Penakluk 3 Suku Kata',
    subtitle: 'Kata Panjang SEPEDA',
    category: 'words',
    moduleTarget: 'wordbuilder',
    icon: '🚲',
    targetCount: 1,
    currentCount: (state) => (state.progress.wordsMastered.map(w => w.toUpperCase()).includes('SEPEDA') ? 1 : 0),
    isUnlocked: (state) => state.progress.wordsMastered.map(w => w.toUpperCase()).includes('SEPEDA'),
    requiredItemsDesc: 'Susun kata 3 suku kata SEPEDA (SE + PE + DA)',
    unlockedSpeech: 'Luar biasa! Kamu berhasil menaklukkan kata tiga suku kata Sepeda!',
    lockedSpeech: 'Penakluk 3 Suku Kata. Ayo susun kata Sepeda di game Susun Kata!',
    colorTheme: {
      badgeBg: 'from-purple-500 to-pink-500',
      border: 'border-purple-400',
      badgeGlow: 'rgba(168, 85, 247, 0.4)',
      pillBg: 'bg-purple-100 text-purple-900',
      textColor: 'text-purple-950',
      accent: '#A855F7',
    },
  },

  // --- 3. MEMBACA DUNIA NYATA & KEBUN ---
  {
    id: 'badge-camera-detective',
    title: 'Detektif Baca Kamera',
    subtitle: 'Cari tulisan di sekitarmu',
    category: 'reading',
    moduleTarget: 'camera',
    icon: '📸',
    targetCount: 2,
    currentCount: (state) =>
      Math.min(2, state.discoveredWords.filter(w => w.source === 'camera').length),
    isUnlocked: (state) =>
      state.discoveredWords.filter(w => w.source === 'camera').length >= 2,
    requiredItemsDesc: 'Temukan dan baca 2 kata dari dunia nyata memakai Kamera',
    unlockedSpeech: 'Hebat! Kamu adalah Detektif Baca Kamera sejati yang suka mencari tulisan di sekitarmu!',
    lockedSpeech: 'Detektif Baca Kamera. Buka Kamera dan foto tulisan di sekitarmu untuk membukanya!',
    colorTheme: {
      badgeBg: 'from-rose-500 to-red-500',
      border: 'border-rose-400',
      badgeGlow: 'rgba(244, 63, 94, 0.4)',
      pillBg: 'bg-rose-100 text-rose-900',
      textColor: 'text-rose-950',
      accent: '#F43F5E',
    },
  },
  {
    id: 'badge-garden-bloom',
    title: 'Pekebun Membaca Rajin',
    subtitle: 'Kebun belajar bertumbuh',
    category: 'reading',
    moduleTarget: 'garden',
    icon: '🌱',
    targetCount: 2,
    currentCount: (state) => Math.min(2, state.garden.stage),
    isUnlocked: (state) => state.garden.stage >= 2,
    requiredItemsDesc: 'Kembangkan kebun belajarmu hingga mencapai Tahap Tanaman Muda',
    unlockedSpeech: 'Indah sekali! Kamu meraih Lencana Pekebun Membaca Rajin karena tanamanmu terus tumbuh subur!',
    lockedSpeech: 'Pekebun Membaca Rajin. Teruslah rajin belajar membaca agar kebunmu bertambah subur!',
    colorTheme: {
      badgeBg: 'from-emerald-500 to-green-600',
      border: 'border-emerald-400',
      badgeGlow: 'rgba(16, 185, 129, 0.4)',
      pillBg: 'bg-emerald-100 text-emerald-900',
      textColor: 'text-emerald-950',
      accent: '#10B981',
    },
  },
];
